import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import {
  deleteHiringRejectionTemplate,
  getHiringResponseSettings,
  postHiringRejectionTemplate,
  putHiringRejectionTemplate,
  putHiringResponseSignature,
} from "../apiClient";
import { createDefaultRejectionReasonTemplates } from "../data/rejectionReasonTemplates";
import { buildDefaultResponseSignature } from "../lib/responseSettings";
import { EMPTY_QUILL_HTML } from "../lib/richTextEditor";

let store = {
  authToken: "",
  loading: false,
  loaded: false,
  error: null,
  signature: "",
  templates: [],
};

const listeners = new Set();
let inflight = null;

function notify() {
  for (const listener of listeners) {
    listener();
  }
}

function setStore(patch) {
  store = { ...store, ...patch };
  notify();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return store;
}

function resetForToken(authToken) {
  const token = String(authToken || "").trim();
  if (store.authToken === token && store.loaded) return;
  if (store.authToken !== token) {
    store = {
      authToken: token,
      loading: false,
      loaded: false,
      error: null,
      signature: "",
      templates: [],
    };
    inflight = null;
    notify();
  }
}

async function loadHiringResponseSettings(authToken) {
  const token = String(authToken || "").trim();
  if (!token) {
    setStore({ authToken: "", loading: false, loaded: false, error: null, signature: "", templates: [] });
    return;
  }
  resetForToken(token);
  if (store.loaded && store.authToken === token) return;
  if (inflight && store.authToken === token) return inflight;

  setStore({ loading: true, error: null });
  inflight = (async () => {
    try {
      const data = await getHiringResponseSettings(token);
      if (store.authToken !== token) return;
      setStore({
        loading: false,
        loaded: true,
        error: null,
        signature: String(data.signature || ""),
        templates: Array.isArray(data.templates) ? data.templates : [],
      });
    } catch (e) {
      if (store.authToken !== token) return;
      setStore({
        loading: false,
        loaded: false,
        error: e instanceof Error ? e.message : "Failed to load response settings",
        signature: "",
        templates: createDefaultRejectionReasonTemplates(),
      });
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

function useHiringResponseSettings(authToken, { companyName } = {}) {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const token = String(authToken || "").trim();

  useEffect(() => {
    if (!token) return;
    void loadHiringResponseSettings(token);
  }, [token]);

  const defaultSignature = useMemo(() => buildDefaultResponseSignature(companyName), [companyName]);
  const savedSignature = snap.signature || defaultSignature;
  const templates =
    snap.loaded && snap.templates.length > 0
      ? snap.templates
      : snap.loading
        ? []
        : snap.templates.length > 0
          ? snap.templates
          : createDefaultRejectionReasonTemplates();

  const getById = useCallback((id) => templates.find((t) => t.id === id), [templates]);

  const updateTemplate = useCallback(
    async (id, patch) => {
      if (!token) throw new Error("Not signed in.");
      const updated = await putHiringRejectionTemplate(token, id, patch);
      setStore({
        templates: templates.map((t) => (t.id === id ? { ...t, ...updated, id: updated.id || id } : t)),
      });
      return updated;
    },
    [token, templates]
  );

  const removeTemplate = useCallback(
    async (id) => {
      if (!token) throw new Error("Not signed in.");
      await deleteHiringRejectionTemplate(token, id);
      setStore({ templates: templates.filter((t) => t.id !== id) });
    },
    [token, templates]
  );

  const addCustomReason = useCallback(async () => {
    if (!token) throw new Error("Not signed in.");
    const created = await postHiringRejectionTemplate(token, {
      label: "",
      responseHtml: EMPTY_QUILL_HTML,
    });
    setStore({ templates: [...templates, created] });
    return created;
  }, [token, templates]);

  const commitSignature = useCallback(
    async (value) => {
      if (!token) throw new Error("Not signed in.");
      const trimmed = String(value || "").trim();
      const data = await putHiringResponseSignature(token, trimmed);
      setStore({ signature: String(data.signature || trimmed) });
      return data.signature || trimmed;
    },
    [token]
  );

  const reload = useCallback(() => {
    if (!token) return Promise.resolve();
    setStore({ loaded: false });
    return loadHiringResponseSettings(token);
  }, [token]);

  return {
    loading: snap.loading,
    loaded: snap.loaded,
    error: snap.error,
    templates,
    savedSignature,
    defaultSignature,
    getById,
    updateTemplate,
    removeTemplate,
    addCustomReason,
    commitSignature,
    reload,
  };
}

export function useRejectionReasonTemplates(authToken) {
  const { loading, loaded, error, templates, getById, updateTemplate, removeTemplate, addCustomReason, reload } =
    useHiringResponseSettings(authToken);
  return { loading, loaded, error, templates, getById, updateTemplate, removeTemplate, addCustomReason, reload };
}

export function useResponseSignature(authToken, companyName) {
  const { loading, loaded, error, savedSignature, defaultSignature, commitSignature, reload } =
    useHiringResponseSettings(authToken, { companyName });
  return { loading, loaded, error, savedSignature, defaultSignature, commitSignature, reload };
}
